/**
 * Today — the glance.
 *
 * The whole point of this screen is that it answers "how am I doing" before
 * any scrolling and before any tap. So it is ordered by what decays fastest:
 * what you have eaten today (changes hourly), what the protocol wants next
 * (changes daily), then the body signals that arrived overnight.
 */
import type { JSX } from 'preact';
import { Card, Empty, Meter, Sparkline, Stat, fmt } from '../ui/bits';
import { checkinToday, compounds, legacyPending, markLegacyMigrated, mealsToday, metricsRange, metricsToday, notify, protocols, recentDoses, refreshAll, settings, today } from '../store';
import { applyLegacy, mapOldState, readLegacyLocalStorage } from '../data/legacy';
import { useState } from 'preact/hooks';
import { total } from '../domain/macros';
import { nextDueDay, scheduledDays } from '../domain/schedule';
import { latestValue, valueOf } from '../data/biometrics';
import { addDays, daysBetween, formatDuration, fromDay, lastNDays } from '../lib/time';

const KG_TO_LB = 2.20462262185;

export function Today(props: { onLogFood: () => void; onLogDose: () => void; onCheckIn: () => void; onTrends: () => void }): JSX.Element {
  const day = today.value;
  const s = settings.value;
  const metrics = metricsToday.value;
  const meals = mealsToday.value;

  const eaten = total(meals.flatMap((m) => m.items));
  const goals = s.goals;

  // The newest weight on record, not only today's: a scale reading from
  // Tuesday is still the number to show on Wednesday, labelled with its day.
  const latestWeight = latestValue(metricsRange.value, 'weightKg');
  const weightKg = latestWeight?.value;
  const displayWeight = weightKg !== undefined
    ? (s.weightUnit === 'lb' ? weightKg * KG_TO_LB : weightKg)
    : undefined;
  const weightAge = latestWeight && latestWeight.day !== day
    ? fromDay(latestWeight.day).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
    : undefined;

  const sleepSec = valueOf(metrics, 'totalSleepSec');
  const readiness = valueOf(metrics, 'readinessScore');
  const hrv = valueOf(metrics, 'hrvMs');
  const rhr = valueOf(metrics, 'restingHr');
  const steps = valueOf(metrics, 'steps');

  // Weight trend over the range we keep loaded.
  const range = metricsRange.value;
  const weightSeries = lastNDays(30, day).map((d) => {
    const row = range.find((r) => r.day === d);
    const kg = valueOf(row, 'weightKg');
    return kg === undefined ? null : (s.weightUnit === 'lb' ? kg * KG_TO_LB : kg);
  });

  return (
    <>
      {legacyPending.value ? <LegacyBanner /> : null}
      <Card title="Today" action={<span class="label">{longDate(day)}</span>}>
        <Meter label="Calories" value={eaten.kcal} goal={goals.kcal} unit=" kcal" />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginTop: '12px' }}>
          <Meter label="Protein" value={eaten.protein} goal={goals.protein} unit="g" mini goodWhenFull />
          <Meter label="Carbs" value={eaten.carbs} goal={goals.carbs} unit="g" mini />
          <Meter label="Fat" value={eaten.fat} goal={goals.fat} unit="g" mini />
        </div>
        {meals.length === 0 ? (
          <p class="note">
            Nothing logged yet today.
          </p>
        ) : (
          <p class="note" style={{ marginTop: '10px' }}>
            {meals.length} {meals.length === 1 ? 'entry' : 'entries'} logged
          </p>
        )}
      </Card>

      <NextDose onLog={props.onLogDose} />

      <Card title="Body" action={<span class="row" style={{ gap: '10px' }}><FreshnessDot /><button class="btn btn-sm btn-ghost" onClick={props.onTrends}>Trends ›</button></span>}>
        <div class="stats">
          <Stat
            label="Weight"
            value={fmt(displayWeight, 1)}
            unit={s.weightUnit}
            {...(weightAge ? { sub: weightAge } : {})}
          />
          <Stat label="Sleep" value={sleepSec ? formatDuration(sleepSec) : '—'} />
          <Stat label="Readiness" value={fmt(readiness)} />
          <Stat label="HRV" value={fmt(hrv)} unit="ms" />
        </div>
        <div class="stats" style={{ borderTop: 'none' }}>
          <Stat label="RHR" value={fmt(rhr)} unit="bpm" />
          <Stat label="Steps" value={steps ? steps.toLocaleString() : '—'} />
          <Stat label="Fat %" value={fmt(valueOf(metrics, 'bodyFatPct'), 1)} unit="" />
          <Stat label="Sl. score" value={fmt(valueOf(metrics, 'sleepScore'))} />
        </div>
        {weightSeries.filter((v) => v !== null).length >= 2 ? (
          <div style={{ marginTop: '12px' }}>
            <div class="row-between" style={{ marginBottom: '4px' }}>
              <span class="label">Weight · 30 days</span>
              <span class="unit">{trendLabel(weightSeries, s.weightUnit)}</span>
            </div>
            <Sparkline values={weightSeries} />
          </div>
        ) : null}
      </Card>

      <CheckInCard onOpen={props.onCheckIn} />
    </>
  );
}

/**
 * The previous Reta lived at this same address, so its data is still in this
 * origin's localStorage. One tap brings it across; nothing is deleted.
 */
function LegacyBanner(): JSX.Element {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    const state = readLegacyLocalStorage();
    if (!state) { await markLegacyMigrated(); return; }
    setBusy(true);
    try {
      const mapped = mapOldState(state);
      const report = await applyLegacy(mapped);
      await markLegacyMigrated();
      await refreshAll();
      const n = Object.values(report.written).reduce((a, b) => a + b, 0) + report.metricsDays;
      notify(`Imported ${n} records from the previous Reta.`);
    } catch (err) {
      notify(err instanceof Error ? err.message : 'Import failed', true);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card title="Previous Reta found">
      <p class="note" style={{ marginBottom: '10px' }}>
        Doses, weigh-ins, meals and settings from the earlier version of this app are still on this
        phone. Import them to keep your history. The originals are left untouched.
      </p>
      <div class="row" style={{ gap: '8px' }}>
        <button class="btn btn-primary grow" disabled={busy} onClick={() => void run()}>{busy ? 'Importing…' : 'Import history'}</button>
        <button class="btn" onClick={() => void markLegacyMigrated()}>Not now</button>
      </div>
    </Card>
  );
}

/** The protocol card: what is due, and the one tap that logs it. */
function NextDose(props: { onLog: () => void }): JSX.Element {
  const day = today.value;
  const active = protocols.value;
  const doses = recentDoses.value;
  const comps = compounds.value;

  if (!active.length) {
    return (
      <Card title="Protocol">
        <Empty>No protocol yet. Add a compound and a schedule to track doses here.</Empty>
      </Card>
    );
  }

  return (
    <Card title="Protocol">
      <div class="stack">
        {active.map((p) => {
          const compound = comps.find((c) => c.id === p.compoundId);
          const takenToday = doses.some((d) => d.compoundId === p.compoundId && d.day === day && !d.skipped);
          const due = nextDueDay(p, day);
          const isDueToday = due === day;
          const lastDose = doses.find((d) => d.compoundId === p.compoundId && !d.skipped);

          return (
            <div key={p.id}>
              <div class="row-between" style={{ marginBottom: '8px' }}>
                <div class="col grow">
                  <span class="row" style={{ gap: '8px' }}>
                    <span class="readout readout-md">{fmtDose(p.doseMg, compound?.displayUnit ?? 'mg')}</span>
                    <span class="unit">{compound?.name ?? 'Compound'}</span>
                  </span>
                  <span class="label" style={{ marginTop: '4px' }}>
                    {takenToday
                      ? 'Logged today'
                      : isDueToday
                        ? 'Due today'
                        : due
                          ? `Next ${relDay(day, due)}`
                          : 'As needed'}
                  </span>
                </div>
                <span class={`badge ${takenToday ? 'is-done' : isDueToday ? 'is-due' : ''}`}>
                  {takenToday ? 'Done' : isDueToday ? 'Due' : 'Scheduled'}
                </span>
              </div>
              <WeekRibbon protocolId={p.id} compoundId={p.compoundId} />
              {lastDose && !takenToday ? (
                <p class="label" style={{ marginTop: '8px', marginBottom: 0 }}>
                  Last · {fmtDose(lastDose.doseMg, compound?.displayUnit ?? 'mg')}
                  {lastDose.site ? ` · ${lastDose.site}` : ''} · {relDay(lastDose.day, day)} ago
                </p>
              ) : null}
            </div>
          );
        })}
        <button class="btn btn-primary btn-full" onClick={props.onLog}>Log a dose</button>
      </div>
    </Card>
  );
}

/** Seven days, marked: taken, due, missed. Same graduated language as the meters. */
function WeekRibbon(props: { protocolId: string; compoundId: string }): JSX.Element {
  const day = today.value;
  const protocol = protocols.value.find((p) => p.id === props.protocolId);
  const doses = recentDoses.value;
  const days = lastNDays(7, day);
  // Ask the scheduler rather than re-deriving it here, so the ribbon agrees
  // with the protocol's start date, end date and interval phase.
  const scheduledSet = new Set(protocol ? scheduledDays(protocol, days[0]!, day) : []);

  return (
    <div class="ribbon">
      {days.map((d) => {
        const taken = doses.some((x) => x.compoundId === props.compoundId && x.day === d && !x.skipped);
        const scheduled = scheduledSet.has(d);
        const missed = scheduled && !taken && d < day;
        const dueToday = scheduled && !taken && d === day;
        return (
          <div key={d} class={`ribbon-day${d === day ? ' is-today' : ''}`}>
            <span class="label">{fromDay(d).toLocaleDateString(undefined, { weekday: 'narrow' })}</span>
            <span class={`ribbon-mark${taken ? ' is-done' : missed ? ' is-missed' : dueToday ? ' is-due' : ''}`} />
          </div>
        );
      })}
    </div>
  );
}

function CheckInCard(props: { onOpen: () => void }): JSX.Element {
  const c = checkinToday.value;
  const filled = c && [c.gi, c.energy, c.hunger, c.cravings].some((v) => typeof v === 'number');
  return (
    <Card
      title="Check-in"
      action={<button class="btn btn-sm btn-ghost" onClick={props.onOpen}>{filled ? 'Edit' : 'Add'}</button>}
    >
      {filled ? (
        <>
          <div class="stats">
            <Stat label="GI" value={fmt(c!.gi)} />
            <Stat label="Energy" value={fmt(c!.energy)} />
            <Stat label="Hunger" value={fmt(c!.hunger)} />
            <Stat label="Cravings" value={fmt(c!.cravings)} />
          </div>
          {c!.giTags?.length ? <p class="note" style={{ marginTop: '8px' }}>GI: {c!.giTags.join(', ')}</p> : null}
        </>
      ) : (
        <Empty>Rate GI, energy, hunger and cravings to see them against your doses over time.</Empty>
      )}
    </Card>
  );
}

/** How recently anything synced. A placard, not a control. */
function FreshnessDot(): JSX.Element {
  const rows = metricsRange.value;
  const latest = rows.reduce<string | null>((acc, r) => (!acc || r.updatedAt > acc ? r.updatedAt : acc), null);
  if (!latest) return <span class="label">No sync yet</span>;
  const ageH = (Date.now() - Date.parse(latest)) / 3_600_000;
  const cls = ageH < 24 ? 'is-fresh' : ageH < 48 ? 'is-aging' : 'is-stale';
  const text = ageH < 1 ? 'just now' : ageH < 48 ? `${Math.round(ageH)}h ago` : `${Math.round(ageH / 24)}d ago`;
  return (
    <span class="row" style={{ gap: '6px' }}>
      <span class={`dot ${cls}`} />
      <span class="label">{text}</span>
    </span>
  );
}

// --------------------------------------------------------------------------

function longDate(day: string): string {
  return fromDay(day).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
}

function relDay(from: string, to: string): string {
  const n = daysBetween(from, to);
  if (n === 0) return 'today';
  if (n === 1) return 'tomorrow';
  if (n === -1) return '1 day';
  return n > 0 ? `in ${n} days` : `${Math.abs(n)} days`;
}

export function fmtDose(mg: number, unit: 'mg' | 'mcg'): string {
  if (unit === 'mcg') return `${fmt(mg * 1000)} mcg`;
  return `${fmt(mg, mg < 1 ? 2 : 2)} mg`;
}

function trendLabel(series: (number | null)[], unit: string): string {
  const real = series.filter((v): v is number => v !== null);
  if (real.length < 2) return '';
  const delta = real[real.length - 1]! - real[0]!;
  const sign = delta > 0 ? '+' : '';
  return `${sign}${delta.toFixed(1)} ${unit}`;
}

void addDays;
